import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { t } from "i18next";
import { Icon, Paragraph, ShortSelect } from "@smartsuite/react-ui/lib";
import { useRootStore } from "../../../../stores/hooks";
import { plural } from "../../../../locales/i18n";
import { ActionSectionWrapper, SectionHeading } from "../../style";
import type { CurrSection } from "../../types";
import type { BranchRef } from "../../../../stores/automation/branches.store";
import { getBranchNameIssues } from "../../../../stores/automation/utils/inputValidation";
import { BranchConditionsForm } from "./BranchConditionsForm";
import {
  BRANCH_NAME_ERROR_COLOR,
  StyledBranchHeaderIconCircle,
  StyledBranchHeaderRow,
  StyledFieldLabel,
  StyledInlineError,
  StyledModeRow,
  StyledTextInput,
} from "./styles";

interface ModeOption {
  value: "conditions" | "else";
  label: string;
}

// ShortSelect is exported as `React.memo(ShortSelectRaw)`, which erases
// ShortSelectRaw's own generic parameter (MemoExoticComponent isn't
// generic), so `<ShortSelect<ModeOption>>` doesn't type-check even though it
// renders correctly at runtime. Re-typing it locally for this one call site
// sidesteps that -- ShortSelectProps itself also isn't re-exported through
// the package's barrel, so this mirrors only the props actually used here
// rather than reaching into the package's internal path for the real type.
interface ModeSelectProps {
  options: ModeOption[];
  value: ModeOption;
  getOptionKey: (option: ModeOption) => string;
  onChange: (option: ModeOption | null) => void;
  selectedTextWeight?: "medium";
  selectedTextSize?: "m";
}
const ModeSelect = ShortSelect as unknown as (
  props: ModeSelectProps,
) => JSX.Element;

const getModeOptionKey = (option: ModeOption): string => option.value;

export const BranchSection = observer<{
  instanceId: number;
  branchHash: string;
  setSection: (section: CurrSection) => void;
}>(({ instanceId, branchHash, setSection }) => {
  const store = useRootStore();
  const candidate = store.automations.candidate;
  const branches = candidate.branches;
  // Stable ref: BranchConditionsForm is memo'd on this single prop, so a
  // fresh object per render (i.e. per name-input keystroke) would re-render
  // the whole conditions editor for nothing.
  const ref: BranchRef = useMemo(
    () => ({ instanceId, branchHash }),
    [instanceId, branchHash],
  );
  const branch = branches.findBranch(ref);
  const block = branches.findBlock(instanceId);
  const isElse = branchHash === "else";

  const modeOptions = useMemo<ModeOption[]>(
    () => [
      { value: "conditions", label: t("branches.mode-if-conditions") },
      { value: "else", label: t("branches.if-no-conditions-met") },
    ],
    [],
  );
  // Only one branch per block may occupy the else slot at a time; once it's
  // taken by another branch, this branch can't switch into it too.
  const elseTaken = block?.elseBranch !== undefined && !isElse;
  const selectOptions = useMemo(
    () => (elseTaken ? [modeOptions[0]] : modeOptions),
    [elseTaken, modeOptions],
  );

  const handleModeChange = useCallback(
    (option: ModeOption | null): void => {
      const currentValue = isElse ? "else" : "conditions";
      if (option === null || option.value === currentValue) return;
      if (branch === undefined) return;

      const ok = branches.setBranchMode(ref, option.value);
      if (!ok) return;

      // setBranchMode preserves the branch's hash in both directions, so the
      // restored conditions branch is re-selected by the hash already in
      // scope -- no positional read-back needed.
      setSection({
        type: "branch",
        instanceId,
        branchHash: option.value === "else" ? "else" : branch.hash,
      });
    },
    [branch, branches, instanceId, isElse, ref, setSection],
  );

  // Keeps data-testid="branch-section" on the root in every case (including
  // an unknown/stale ref) -- SectionSwitch's routing test relies on it being
  // present unconditionally.
  if (branch === undefined || block === undefined) {
    return <div data-testid="branch-section" />;
  }

  const nameIssue = getBranchNameIssues(block).get(branch.hash);
  const nameErrorKey =
    nameIssue === "empty"
      ? "branches.name-required-error"
      : nameIssue === "duplicate"
        ? "branches.name-duplicate-error"
        : undefined;

  const currentMode: ModeOption = isElse ? modeOptions[1] : modeOptions[0];

  const summary = branches.conditionSummary(ref);
  const summaryText =
    typeof summary === "number"
      ? `${summary} ${t(plural("branches.condition-unit", summary))}`
      : summary === "else"
        ? t("branches.if-no-conditions-met")
        : t("branches.no-conditions");

  return (
    <ActionSectionWrapper data-testid="branch-section" theme={{}}>
      <SectionHeading>{t("branches.branch-settings-title")}</SectionHeading>
      <StyledBranchHeaderRow>
        <StyledBranchHeaderIconCircle>
          <Icon
            name="checklist"
            size="MISC"
            dimension={{ width: 20, height: 20 }}
            color="currentColor"
          />
        </StyledBranchHeaderIconCircle>
        <div>
          <Paragraph weight="semibold" size="m">
            {branch.label}
          </Paragraph>
          <Paragraph color="secondary" size="xs">
            {summaryText}
          </Paragraph>
        </div>
      </StyledBranchHeaderRow>

      <StyledFieldLabel htmlFor="cl-branch-name">
        {t("branches.branch-name-label")}
      </StyledFieldLabel>
      <StyledTextInput
        id="cl-branch-name"
        data-testid="branch-name-input"
        aria-describedby={
          nameErrorKey !== undefined ? "cl-branch-name-error" : undefined
        }
        value={branch.label}
        onChange={(e) => branches.setBranchLabel(ref, e.target.value)}
        style={
          nameErrorKey !== undefined
            ? { borderColor: BRANCH_NAME_ERROR_COLOR }
            : undefined
        }
      />
      {nameErrorKey !== undefined && (
        <StyledInlineError id="cl-branch-name-error" role="alert">
          {t(nameErrorKey)}
        </StyledInlineError>
      )}

      <StyledModeRow>
        <ModeSelect
          options={selectOptions}
          value={currentMode}
          getOptionKey={getModeOptionKey}
          onChange={handleModeChange}
          selectedTextWeight="medium"
          selectedTextSize="m"
        />
      </StyledModeRow>

      {!isElse && <BranchConditionsForm branchRef={ref} />}
      {/* Only after a save attempt (submit) -- a freshly created branch starts
          with no conditions, so showing this immediately would flag a state the
          user hasn't done anything wrong in yet. Mirrors BranchHeaderCard's
          warning-icon gate. */}
      {!isElse &&
        candidate.isTriggerSubmitted &&
        "conditions" in branch &&
        (branch.conditions?.group ?? []).length === 0 && (
          <StyledInlineError role="alert">
            {t("branches.conditions-required-error")}
          </StyledInlineError>
        )}
    </ActionSectionWrapper>
  );
});
BranchSection.displayName = "BranchSection";
